import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Icon } from "@/components/Icon";
import logoStacked from "../assets/pnrbet-stacked.png";
import { PUBLIC_LEGAL_PATHS } from "../legal/registry";

// Network / server error while checking location (UIA-09/UIA-19) — this is NOT a jurisdiction
// ban, so we offer a Retry instead of locking the player out behind the ban screen.
function GeoRetry({ onRetry }) {
  return (
    <div className="relative flex min-h-[100dvh] flex-col items-center justify-center bg-surface-1 px-6 py-[max(1.5rem,env(safe-area-inset-top))] pb-[max(1.5rem,env(safe-area-inset-bottom))] text-center text-white" data-testid="geo-network-error">
      <div className="grain" />
      <div className="relative z-10 w-full max-w-md">
        <div className="mb-8 flex items-center justify-center">
          <img src={logoStacked} alt="PRYMEBET" className="h-[81px] w-auto" />
        </div>
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-pnr-green/15">
          <Icon name="refresh" size={32} className="text-pnr-green" />
        </div>
        <h1 className="font-display text-3xl font-black tracking-tighter">Couldn&apos;t verify your location</h1>
        <p className="mt-4 text-sm leading-relaxed text-meta">
          We weren&apos;t able to reach our location service. This is usually temporary — please check your
          connection and try again.
        </p>
        <div className="mt-8">
          <Button onClick={onRetry} data-testid="geo-retry"
            className="h-11 rounded-full bg-pnr-green px-6 font-bold text-surface-1 transition-colors hover:bg-pnr-green-hover focus-ring">
            <Icon name="refresh" size={14} className="mr-1.5" /> Retry
          </Button>
        </div>
        <p className="mt-6 text-xs text-[#71717A]">
          Still stuck? <Link to="/support" className="text-pnr-green underline underline-offset-2 hover:text-white focus-ring">Contact support</Link>.
        </p>
      </div>
    </div>
  );
}

function GeoBlock({ stateName, bannedStates, ipBanned }) {
  const { login } = useAuth();
  const [showAdmin, setShowAdmin] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const adminLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      const u = data.user;
      const allowed = u?.role === "admin" || u?.role === "owner" || u?.role === "super_admin" || u?.geo_exempt;
      if (!allowed) {
        toast.error("This service is unavailable in your location.");
        return;
      }
      login(data.token, u);
      toast.success("Access granted");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const states = bannedStates && bannedStates.length ? bannedStates.join(" and ") : "your location";

  return (
    <div className="relative flex min-h-[100dvh] flex-col items-center justify-center bg-surface-1 px-6 py-[max(1.5rem,env(safe-area-inset-top))] pb-[max(1.5rem,env(safe-area-inset-bottom))] text-center text-white" data-testid="geo-block-screen">
      <div className="grain" />
      <div className="relative z-10 w-full max-w-md">
        <div className="mb-8 flex items-center justify-center">
          <img src={logoStacked} alt="PRYMEBET" className="h-[81px] w-auto" />
        </div>

        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-pnr-red/15">
          <Icon name="shield-alert" size={32} className="text-pnr-red" />
        </div>

        {ipBanned ? (
          <>
            <h1 className="font-display text-3xl font-black tracking-tighter" data-testid="ip-ban-heading">Access denied</h1>
            <p className="mt-4 text-sm leading-relaxed text-meta">
              Your access to PRYMEBET has been <span className="font-semibold text-white">blocked</span>. If you believe this is a mistake, please contact support.
            </p>
          </>
        ) : (
          <>
            <h1 className="font-display text-3xl font-black tracking-tighter">Access unavailable in {stateName || "your area"}</h1>
            <p className="mt-4 text-sm leading-relaxed text-meta">
              Due to state regulations, PRYMEBET is not available in <span className="font-semibold text-white">{states}</span>.
              We're unable to offer our services to players located in these states.
            </p>
            <p className="mt-3 text-xs text-[#71717A]">
              If you believe you are seeing this in error, you may be using a VPN or your location could not be verified.
            </p>
          </>
        )}

        <div className="mt-6 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs">
          <Link to="/support" data-testid="geo-support-link" className="text-pnr-green underline underline-offset-2 hover:text-white focus-ring">Contact support</Link>
          {!ipBanned && (
            <Link to="/eligible-jurisdictions" data-testid="geo-eligible-link" className="text-pnr-green underline underline-offset-2 hover:text-white focus-ring">Where PRYMEBET is available</Link>
          )}
        </div>

        <div className="mt-10">
          {!showAdmin ? (
            <button
              data-testid="geo-admin-toggle"
              onClick={() => setShowAdmin(true)}
              className="inline-flex items-center gap-1.5 text-xs text-[#52525B] transition-colors hover:text-meta focus-ring"
            >
              <Icon name="lock" size={12} /> Admin access
            </button>
          ) : (
            <form onSubmit={adminLogin} className="mx-auto max-w-xs space-y-3 rounded-2xl border border-surface-border bg-surface-4 p-5 text-left shadow-[var(--elev-2)]">
              <p className="text-center text-[10px] font-extrabold uppercase tracking-[0.2em] text-[#71717A]">Admin sign in</p>
              <div>
                <Label htmlFor="geo-email" className="text-xs text-meta">Email</Label>
                <Input id="geo-email" type="email" required data-testid="geo-admin-email"
                  autoComplete="email" inputMode="email" autoCapitalize="none" autoCorrect="off" spellCheck={false}
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  className="mt-1.5 border-surface-border bg-surface-1 text-white focus-ring focus-visible:border-pnr-green" placeholder="admin@prymebet.app" />
              </div>
              <div>
                <Label htmlFor="geo-pass" className="text-xs text-meta">Password</Label>
                <Input id="geo-pass" type="password" required data-testid="geo-admin-password"
                  autoComplete="current-password" enterKeyHint="go"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  className="mt-1.5 border-surface-border bg-surface-1 text-white focus-ring focus-visible:border-pnr-green" placeholder="••••••••" />
              </div>
              <Button type="submit" disabled={loading} data-testid="geo-admin-submit"
                className="h-11 w-full rounded-full bg-pnr-green font-bold text-surface-1 transition-colors hover:bg-pnr-green-hover focus-ring disabled:cursor-not-allowed disabled:bg-pnr-green/40 disabled:text-surface-1/60">
                {loading ? "Please wait..." : "Sign in"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export function GeoGate({ children }) {
  const { user } = useAuth();
  const [geo, setGeo] = useState({ loading: true, allowed: true, stateName: null, bannedStates: [], ipBanned: false, networkError: false });
  const [attempt, setAttempt] = useState(0);

  // Public legal/utility pages are always reachable, even from restricted locations.
  const path = typeof window !== "undefined" ? window.location.pathname : "";
  const isExemptPath =
    path === "/reset-password" ||
    PUBLIC_LEGAL_PATHS.includes(path) ||
    path.startsWith("/dealer/blackjack/");

  useEffect(() => {
    let active = true;
    if (isExemptPath) { setGeo({ loading: false, allowed: true, stateName: null, bannedStates: [], ipBanned: false, networkError: false }); return; }
    setGeo((g) => ({ ...g, loading: true, networkError: false }));
    api.get("/geo/check")
      .then(({ data }) => {
        if (active) setGeo({ loading: false, allowed: data.allowed, stateName: data.state_name, bannedStates: data.banned_states || [], ipBanned: !!data.ip_banned, networkError: false });
      })
      .catch((err) => {
        if (!active) return;
        // Distinguish a network / server failure (no response, or a 5xx) — which is NOT a
        // jurisdiction decision — from a genuine block. A real ban is a 200 with allowed:false
        // (handled above), so an error here means we simply couldn't verify. Show a retry path
        // instead of locking the player out behind the ban screen (UIA-09/UIA-19).
        const status = err?.response?.status;
        const isNetworkOrServer = !err?.response || (status >= 500 && status < 600);
        if (isNetworkOrServer) {
          setGeo({ loading: false, allowed: false, stateName: null, bannedStates: [], ipBanned: false, networkError: true });
        } else {
          // A 4xx (e.g. an explicit forbidden) is treated as a genuine block — fail-closed,
          // admins can still sign in.
          setGeo({ loading: false, allowed: false, stateName: null, bannedStates: ["Washington", "Idaho", "Michigan", "Nevada", "Louisiana", "Montana", "Kentucky"], ipBanned: false, networkError: false });
        }
      });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt]);

  if (geo.loading) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-surface-1">
        <Icon name="refresh" size={24} className="animate-spin text-pnr-green" />
      </div>
    );
  }

  const isAdmin = user && (user.role === "admin" || user.role === "owner" || user.role === "super_admin");
  const isExempt = user && user.geo_exempt;
  // A network/verification failure is not a ban — offer retry (admins/exempt still pass through).
  if (geo.networkError && !isAdmin && !isExempt) {
    return <GeoRetry onRetry={() => setAttempt((a) => a + 1)} />;
  }
  if (!geo.allowed && !isAdmin && !isExempt) {
    return <GeoBlock stateName={geo.stateName} bannedStates={geo.bannedStates} ipBanned={geo.ipBanned} />;
  }

  return children;
}
